import { useEffect, useMemo, useState } from 'react';

import { DrawingItem, DrawingShapeHookReturnType } from '../drawingShape.types';
import { getPricedSinksFromDrawingShapes } from '../drawingShape.utils';

import { drawingTool } from '~/core/engine/DrawingTool';
import { ShapeType } from '~/core/enums';
import { DrawingAppliance, DrawingShape, ProductQty } from '~/core/models';
import { makeApplianceName } from '~/core/utils';
import { useAppSelector } from '~/redux/hooks';
import { selectActiveDrawingObject } from '~/redux/slices/drawingShape';

export const useDrawingShape = (): DrawingShapeHookReturnType => {
  const [drawingShapes, setDrawingShapes] = useState<DrawingShape[]>([
    drawingTool.buildFauxShape(),
  ]);
  const [drawingAppliances, setDrawingAppliances] = useState<
    DrawingAppliance[]
  >([]);
  const [showNotification, updateShowNotification] = useState<
    Record<string, boolean>
  >({});
  const [
    isCornerRadiusConstraintModalOpen,
    setIsCornerRadiusConstraintModalOpen,
  ] = useState(false);

  const activeDrawingObject = useAppSelector(selectActiveDrawingObject);

  const actualDrawingShapes = useMemo(
    () => drawingShapes.filter(({ type }) => type !== ShapeType.FAUX_SHAPE),
    [drawingShapes]
  );
  const defaultActiveDrawingShape = useMemo(
    () => actualDrawingShapes[0],
    [actualDrawingShapes]
  );

  /** Any active item in the canvas, by default use first valid shape */
  const activeDrawingItem: DrawingItem | undefined = useMemo(() => {
    if (!activeDrawingObject || !activeDrawingObject.object) {
      return { type: 'shape', item: defaultActiveDrawingShape };
    }
    const { id, type } = activeDrawingObject.object;

    if (type === 'appliance') {
      return { type, item: id };
    } else {
      return { type, item: drawingShapes.find((shape) => shape.id === id) };
    }
  }, [activeDrawingObject, defaultActiveDrawingShape, drawingShapes]);

  const numberOfShapesInTheDrawingArea = useMemo(
    () =>
      drawingShapes.filter((shape) =>
        shape.id === ShapeType.FAUX_SHAPE ? shape.appliances.length > 0 : true
      ).length,
    [drawingShapes]
  );

  const selectedSinksInDrawing: ProductQty[] = useMemo(
    () => getPricedSinksFromDrawingShapes(drawingShapes),
    [drawingShapes]
  );

  const activeDrawingShape = useMemo(
    () =>
      activeDrawingItem?.type === 'shape'
        ? (activeDrawingItem.item as DrawingShape)
        : undefined,
    [activeDrawingItem]
  );

  const activeDrawingApplianceId = useMemo(
    () =>
      activeDrawingItem?.type === 'appliance'
        ? (activeDrawingItem.item as string)
        : undefined,
    [activeDrawingItem]
  );

  const activeDrawingAppliance = useMemo(
    () =>
      drawingShapes
        .flatMap((shape) => shape.appliances)
        .find(
          (appliance) =>
            makeApplianceName(appliance.id) === activeDrawingApplianceId
        ),
    [activeDrawingApplianceId, drawingShapes]
  );

  const areThereShapesInTheDrawingArea = useMemo(
    () => numberOfShapesInTheDrawingArea > 0,
    [numberOfShapesInTheDrawingArea]
  );

  /**Notifications */
  useEffect(() => {
    drawingShapes.forEach((shape) => {
      if (!showNotification[shape.id]) {
        updateShowNotification({
          ...showNotification,
          [shape.id]: true,
        });
      }
    });
  }, [drawingShapes, showNotification]);

  const getters: DrawingShapeHookReturnType['getters'] = {
    activeDrawingApplianceId,
    activeDrawingAppliance,
    activeDrawingItem,
    activeDrawingShape,
    activeDrawingObject,
    actualDrawingShapes,
    areThereShapesInTheDrawingArea,
    drawingAppliances,
    drawingShapes,
    numberOfShapesInTheDrawingArea,
    showNotification,
    selectedSinksInDrawing,
    isCornerRadiusConstraintModalOpen,
  };
  const handlers: DrawingShapeHookReturnType['handlers'] = {
    setDrawingAppliances,
    setDrawingShapes,
    updateShowNotification,
    setIsCornerRadiusConstraintModalOpen,
  };

  return {
    getters,
    handlers,
  };
};
